/**
 * Parser for WhatsApp's exported `_chat.txt`.
 *
 * Pure: string in, objects out. No DOM, no fetch, no crypto — so it can be
 * exercised directly in Node by tools/whatsapp-parser-check.mjs.
 *
 * The three things that actually break real exports, and what we do:
 *
 *  1. TWO LINE FORMATS.
 *       iOS      [04/08/2026, 09:12:41] Ian Leong: text
 *       Android  04/08/2026, 09:12 - Ian Leong: text
 *     Both are tried per line.
 *
 *  2. DAY/MONTH ORDER IS LOCALE-DEPENDENT and the file never says which.
 *     Reading 04/08 as 8 April instead of 4 August silently shifts every range
 *     by months, and nothing looks broken. We settle it from evidence: any
 *     component above 12 can only be the day. If the whole file is ambiguous
 *     (every date <= 12/12) we fall back to which ordering keeps timestamps
 *     non-decreasing — an export is always chronological. If both survive that,
 *     we say so via `dateOrderConfidence` and the UI warns rather than guessing
 *     quietly.
 *
 *  3. NO TIMEZONE. The times are whatever the exporting phone was showing.
 *     We build Dates from local components and never convert, so the picker
 *     (also local wall-clock) compares like with like. Any conversion here
 *     would be a fabrication.
 */

export type MediaKind = "image" | "video" | "audio" | "document" | "sticker" | "unknown";

export interface ChatMessage {
  /** 1-based position in the file — the citable line number. */
  n: number;
  /** Local wall-clock instant, exactly as written in the export. */
  at: Date;
  /** Null for system notices ("Messages and calls are end-to-end encrypted"). */
  sender: string | null;
  text: string;
  /** Set when the line references a media file. */
  media?: { filename: string | null; kind: MediaKind };
}

export interface ParsedChat {
  messages: ChatMessage[];
  dateOrder: "dmy" | "mdy";
  /** "proven" — a component >12 settled it. "inferred" — chronology settled it. "ambiguous" — neither could. */
  dateOrderConfidence: "proven" | "inferred" | "ambiguous";
  /** Distinct senders, most frequent first. */
  senders: string[];
  warnings: string[];
  /** Lines that matched no known format and no continuation. */
  unparsedLines: number;
}

/** Strip the bidi/format marks iOS sprinkles through the file, normalise spaces. */
function normalise(raw: string): string {
  return raw
    .replace(/^\uFEFF/, "")                    // byte-order mark
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "") // bidi marks iOS sprinkles in
    .replace(/[\u00A0\u202F]/g, " ")           // nbsp + narrow nbsp (iOS 17 puts one before AM/PM)
    .replace(/\r\n?/g, "\n");
}

/** iOS: [d/m/yyyy, h:mm:ss AM] rest */
const IOS_HEAD =
  /^\[(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4}),\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AaPp])\.?[Mm]\.?)?\]\s*([\s\S]*)$/;
/** Android: d/m/yyyy, h:mm AM - rest */
const ANDROID_HEAD =
  /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4}),\s+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*([AaPp])\.?[Mm]\.?)?\s+-\s+([\s\S]*)$/;

interface RawLine {
  a: number; // first date component
  b: number; // second date component
  year: number;
  hh: number;
  mm: number;
  ss: number;
  rest: string;
}

function matchHeader(line: string): RawLine | null {
  const m = IOS_HEAD.exec(line) ?? ANDROID_HEAD.exec(line);
  if (!m) return null;
  const [, aS, bS, yS, hS, minS, secS, ampm, rest] = m;
  let hh = Number(hS);
  const ampmLower = ampm?.toLowerCase();
  if (ampmLower === "p" && hh < 12) hh += 12;
  if (ampmLower === "a" && hh === 12) hh = 0;
  let year = Number(yS);
  if (year < 100) year += 2000;
  return {
    a: Number(aS),
    b: Number(bS),
    year,
    hh,
    mm: Number(minS),
    ss: secS ? Number(secS) : 0,
    rest: rest ?? "",
  };
}

/** Build a Date from local components — never a UTC conversion. */
function toDate(r: RawLine, order: "dmy" | "mdy"): Date {
  const day = order === "dmy" ? r.a : r.b;
  const month = order === "dmy" ? r.b : r.a;
  return new Date(r.year, month - 1, day, r.hh, r.mm, r.ss, 0);
}

function isChronological(rows: RawLine[], order: "dmy" | "mdy"): boolean {
  let prev = -Infinity;
  for (const r of rows) {
    const day = order === "dmy" ? r.a : r.b;
    const month = order === "dmy" ? r.b : r.a;
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    const t = toDate(r, order).getTime();
    if (Number.isNaN(t)) return false;
    if (t < prev) return false;
    prev = t;
  }
  return true;
}

function decideDateOrder(rows: RawLine[]): {
  order: "dmy" | "mdy";
  confidence: ParsedChat["dateOrderConfidence"];
} {
  const firstOver12 = rows.some((r) => r.a > 12);
  const secondOver12 = rows.some((r) => r.b > 12);
  // A value above 12 can only be a day. Both being over 12 means the file is
  // internally inconsistent; trust the first position (the common layout).
  if (firstOver12 && !secondOver12) return { order: "dmy", confidence: "proven" };
  if (secondOver12 && !firstOver12) return { order: "mdy", confidence: "proven" };
  if (firstOver12 && secondOver12) return { order: "dmy", confidence: "ambiguous" };

  const dmyOk = isChronological(rows, "dmy");
  const mdyOk = isChronological(rows, "mdy");
  if (dmyOk && !mdyOk) return { order: "dmy", confidence: "inferred" };
  if (mdyOk && !dmyOk) return { order: "mdy", confidence: "inferred" };
  // Both readings hold (e.g. a handful of messages inside one month). Default to
  // day-first, which is what Singapore phones produce, and flag it.
  return { order: "dmy", confidence: "ambiguous" };
}

const EXT_KIND: Array<[RegExp, MediaKind]> = [
  [/\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/i, "image"],
  [/\.(mp4|mov|3gp|avi|mkv|webm|m4v)$/i, "video"],
  [/\.(opus|ogg|m4a|mp3|aac|amr|wav)$/i, "audio"],
  [/\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rtf|odt)$/i, "document"],
  [/\.webp$/i, "sticker"],
];

export function mediaKindOf(filename: string): MediaKind {
  for (const [re, kind] of EXT_KIND) if (re.test(filename)) return kind;
  return "unknown";
}

/** `<attached: FILE>` (iOS), `FILE (file attached)` (Android), and older shapes. */
const ATTACH_PATTERNS: RegExp[] = [
  /<attached:\s*([^>]+)>/i,
  /^\s*([^\s].{0,200}?)\s*\(file attached\)\s*$/i,
  /<attached\s+([^>]+)>/i,
];
const OMITTED =
  /^\s*(?:<)?(?:media|image|video|audio|sticker|gif|document|photo)\s+omitted(?:>)?\s*$/i;
/** Cheap gate: does this line contain something shaped like `name.ext`? */
const LOOKS_LIKE_FILENAME = /\.[a-z0-9]{3,4}\b/i;

/**
 * Pull a media reference out of a message body.
 * `known` is the set of media filenames actually present in the archive — when
 * supplied it acts as a last-resort matcher, which is what keeps non-English
 * exports working (the bracketed wording is localised; the filename is not).
 */
function extractMedia(
  body: string,
  known?: Set<string>,
): { filename: string | null; kind: MediaKind; cleaned: string } | null {
  for (const re of ATTACH_PATTERNS) {
    const m = re.exec(body);
    if (m?.[1]) {
      const filename = m[1].trim();
      return {
        filename,
        kind: mediaKindOf(filename),
        cleaned: body.replace(re, "").trim(),
      };
    }
  }
  if (OMITTED.test(body)) {
    return { filename: null, kind: "unknown", cleaned: "" };
  }
  if (known && known.size > 0) {
    const trimmed = body.trim();
    if (known.has(trimmed)) {
      return { filename: trimmed, kind: mediaKindOf(trimmed), cleaned: "" };
    }
    // The scan below is O(media files) per message. On a 5,000-message chat
    // with 250 media files that is over a million string compares on a phone,
    // almost all of it wasted on ordinary prose. Only bother when the line
    // actually looks like it carries a filename.
    if (!LOOKS_LIKE_FILENAME.test(trimmed)) return null;
    for (const name of known) {
      if (name.length > 6 && trimmed.includes(name)) {
        return {
          filename: name,
          kind: mediaKindOf(name),
          cleaned: trimmed.replace(name, "").trim(),
        };
      }
    }
  }
  return null;
}

/** Split "Sender: body" — senders never contain a colon in practice. */
const SENDER_SPLIT = /^([^:\n]{1,80}):[ \t]?([\s\S]*)$/;

export function parseChat(raw: string, mediaNames?: Iterable<string>): ParsedChat {
  const text = normalise(raw);
  const lines = text.split("\n");
  const warnings: string[] = [];
  const known = mediaNames ? new Set(mediaNames) : undefined;

  // --- pass 1: headers only, to settle date order and the sender vocabulary ---
  const rows: RawLine[] = [];
  const senderCount = new Map<string, number>();
  for (const line of lines) {
    const head = matchHeader(line);
    if (!head) continue;
    rows.push(head);
    const sm = SENDER_SPLIT.exec(head.rest);
    if (sm) senderCount.set(sm[1]!.trim(), (senderCount.get(sm[1]!.trim()) ?? 0) + 1);
  }
  if (rows.length === 0) {
    return {
      messages: [],
      dateOrder: "dmy",
      dateOrderConfidence: "ambiguous",
      senders: [],
      warnings: ["No messages found — this doesn't look like a WhatsApp chat export."],
      unparsedLines: lines.length,
    };
  }

  const { order, confidence } = decideDateOrder(rows);
  if (confidence === "ambiguous") {
    warnings.push(
      "Every date in this export could be read either day-first or month-first. Assuming day-first (04/08 = 4 August). Check the first and last message before sending.",
    );
  }

  // A real participant speaks more than once. A one-off "You changed the
  // subject to: x" would otherwise masquerade as a sender named
  // "You changed the subject to".
  const realSenders = new Set(
    [...senderCount.entries()].filter(([, c]) => c >= 2).map(([s]) => s),
  );

  // --- pass 2: build messages, folding continuation lines into the previous ---
  const messages: ChatMessage[] = [];
  let unparsedLines = 0;
  let n = 0;
  for (const line of lines) {
    const head = matchHeader(line);
    if (!head) {
      if (line.trim() === "") {
        if (messages.length > 0) messages[messages.length - 1]!.text += "\n";
        continue;
      }
      if (messages.length > 0) {
        // Continuation of a multi-line message.
        const prev = messages[messages.length - 1]!;
        prev.text = prev.text ? `${prev.text}\n${line}` : line;
      } else {
        unparsedLines++;
      }
      continue;
    }
    n++;
    let sender: string | null = null;
    let body = head.rest;
    const sm = SENDER_SPLIT.exec(head.rest);
    if (sm && realSenders.has(sm[1]!.trim())) {
      sender = sm[1]!.trim();
      body = sm[2] ?? "";
    }
    const at = toDate(head, order);
    const msg: ChatMessage = { n, at, sender, text: body };
    const media = extractMedia(body, known);
    if (media) {
      msg.media = { filename: media.filename, kind: media.kind };
      msg.text = media.cleaned;
    }
    messages.push(msg);
  }

  // Trailing newlines from the blank-line handling above.
  for (const m of messages) m.text = m.text.replace(/\n+$/, "");

  const senders = [...senderCount.entries()]
    .filter(([s]) => realSenders.has(s))
    .sort((a, b) => b[1] - a[1])
    .map(([s]) => s);

  return { messages, dateOrder: order, dateOrderConfidence: confidence, senders, warnings, unparsedLines };
}
